import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Store } from '../Store';
import { getError } from '../utils';
import { api } from '../Api';

const RANGES = [
  { key: 'today', label: 'Today', caption: 'Joined today' },
  { key: 'week', label: 'Week', caption: 'Last 7 days' },
  { key: 'month', label: 'Month', caption: 'Last 30 days' },
  { key: '3months', label: '3 Months', caption: 'Last 3 months' },
  { key: 'total', label: 'Total', caption: 'All time' },
];

const EMPTY_RANGE = { left: 0, right: 0, credit: 0 };

const fmt = (value) =>
  value === null || value === undefined
    ? '--'
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function TeamSales() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();

  const placement = new URLSearchParams(window.location.search).get('placement');
  const username = placement || userInfo?.username;

  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState('total');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSummary = useCallback(async () => {
    if (!userInfo || !username) return;
    try {
      setLoading(true);
      setError('');
      const { data } = await axios.post(
        api + '/api/users/teamSalesSummary',
        { username },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      setSummary(data);
    } catch (err) {
      setError(getError(err));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [userInfo, username]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Every range arrives in one response, so switching tabs is instant.
  const active = useMemo(
    () => (summary && summary.ranges && summary.ranges[range]) || EMPTY_RANGE,
    [summary, range]
  );

  const activeCaption =
    RANGES.find((item) => item.key === range)?.caption || '';

  if (!userInfo) {
    return (
      <Container className="no-gutter">
        <div style={s.card}>
          <div style={s.state}>Please log in to view your sales team.</div>
        </div>
      </Container>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Sales Team</title>
      </Helmet>

      <Container className="no-gutter">
        <div style={s.card}>
          <div style={s.logoWrap}>
            <img
              src="https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png"
              style={s.logo}
              alt="ClickCart Logo"
            />
          </div>

          <div style={s.header}>
            <button
              type="button"
              style={s.backBtn}
              onClick={() => navigate('/user-panel')}
              aria-label="Back to user panel"
            >
              <i className="fa fa-long-arrow-left" aria-hidden="true" />
            </button>
            <div style={s.headerText}>
              <div style={s.headerTitle}>Sales Team</div>
              <div style={s.headerSub}>
                {summary ? `@${summary.username}` : username ? `@${username}` : ''}
              </div>
            </div>
            <button
              type="button"
              style={s.refreshBtn}
              onClick={loadSummary}
              disabled={loading}
              aria-label="Refresh"
            >
              <i
                className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'}`}
                aria-hidden="true"
              />
            </button>
          </div>

          <div style={s.body}>
            <div style={s.rangeBar}>
              {RANGES.map((item) => {
                const on = range === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setRange(item.key)}
                    style={{ ...s.rangeBtn, ...(on ? s.rangeBtnOn : null) }}
                    aria-pressed={on}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div style={s.caption}>{activeCaption}</div>

            {error ? (
              <div style={s.error}>
                <i className="fa fa-exclamation-triangle" aria-hidden="true" />{' '}
                {error}
                <button type="button" style={s.retryBtn} onClick={loadSummary}>
                  Try again
                </button>
              </div>
            ) : null}

            {summary && summary.stale ? (
              <div style={s.warn}>
                <i className="fa fa-info-circle" aria-hidden="true" /> Your team
                is large, so only the all-time totals could be loaded.
              </div>
            ) : null}

            <div style={s.grid}>
              <StatCard
                title="Team Sales A"
                subtitle="Left leg"
                value={active.left}
                loading={loading && !summary}
              />
              <StatCard
                title="Team Sales B"
                subtitle="Right leg"
                value={active.right}
                loading={loading && !summary}
              />
            </div>

            <StatCard
              title="My Credit"
              subtitle="Difference between both legs"
              value={active.credit}
              loading={loading && !summary}
              wide
            />

            {summary ? (
              <div style={s.footNote}>
                Each account counts as {fmt(summary.perAccount)} points.
              </div>
            ) : null}
          </div>
        </div>
      </Container>
    </div>
  );
}

function StatCard({ title, subtitle, value, loading, wide }) {
  return (
    <div style={{ ...s.stat, ...(wide ? s.statWide : null) }}>
      <div style={s.statTitle}>{title}</div>
      <div style={s.statValue}>
        {loading ? <i className="fa fa-spinner fa-spin" aria-hidden="true" /> : fmt(value)}
      </div>
      <div style={s.statSub}>{subtitle}</div>
    </div>
  );
}

const s = {
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    margin: '16px auto',
    maxWidth: 720,
  },
  logoWrap: { textAlign: 'center', padding: '24px 16px 8px' },
  logo: { width: '100%', maxWidth: 220 },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    background: '#0d6efd',
    color: '#fff',
  },
  headerText: { flex: 1, minWidth: 0 },
  headerTitle: { fontSize: 18, fontWeight: 800, lineHeight: 1.2 },
  headerSub: {
    fontSize: 12,
    opacity: 0.85,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  backBtn: {
    border: 'none',
    background: 'rgba(255,255,255,0.18)',
    color: '#fff',
    width: 36,
    height: 36,
    borderRadius: '50%',
    cursor: 'pointer',
    flexShrink: 0,
  },
  refreshBtn: {
    border: 'none',
    background: 'rgba(255,255,255,0.18)',
    color: '#fff',
    width: 36,
    height: 36,
    borderRadius: '50%',
    cursor: 'pointer',
    flexShrink: 0,
  },
  body: { padding: 16 },
  rangeBar: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    paddingBottom: 4,
    WebkitOverflowScrolling: 'touch',
  },
  rangeBtn: {
    flex: '0 0 auto',
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid #d7dce3',
    background: '#fff',
    color: '#41506b',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  rangeBtnOn: {
    background: '#0d6efd',
    borderColor: '#0d6efd',
    color: '#fff',
  },
  caption: { margin: '12px 0 4px', fontSize: 12, color: '#6b7280', fontWeight: 600 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginTop: 8,
  },
  stat: {
    background: 'linear-gradient(135deg, #42b4e7 0%, #2b8fd1 100%)',
    color: '#fff',
    borderRadius: 12,
    padding: '18px 14px',
    textAlign: 'center',
    minWidth: 0,
  },
  statWide: {
    marginTop: 12,
    background: 'linear-gradient(135deg, #16a34a 0%, #0f8a3d 100%)',
  },
  statTitle: { fontSize: 13, fontWeight: 700, opacity: 0.92 },
  statValue: {
    fontSize: 'clamp(26px, 7vw, 40px)',
    fontWeight: 800,
    lineHeight: 1.15,
    margin: '6px 0',
    wordBreak: 'break-word',
  },
  statSub: { fontSize: 11, opacity: 0.85 },
  error: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #fecaca',
    background: '#fff5f5',
    color: '#b91c1c',
    fontSize: 13,
    fontWeight: 600,
  },
  warn: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #fde68a',
    background: '#fffbeb',
    color: '#92400e',
    fontSize: 13,
    fontWeight: 600,
  },
  retryBtn: {
    marginLeft: 10,
    border: '1px solid #b91c1c',
    background: 'transparent',
    color: '#b91c1c',
    borderRadius: 6,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  footNote: { marginTop: 14, fontSize: 12, color: '#6b7280', textAlign: 'center' },
  state: { padding: 32, textAlign: 'center', color: '#6b7280' },
};
